import { DefaultColors } from "@dreambigger/design-system/src/hooks/use-theme/themes/default";
import {
  CollectionCommunityFeedItem,
  EventCommunityFeedItem,
  StoryCommunityFeedItem,
  Story,
  Event,
} from ".";

export interface Community {
  id: number;
  slug: string;
  title: string;
  eventCount: number;
  storyCount: number;
  meta?: Meta;
  organization: {
    id: number;
    slug: string;
    name: string;
    logoId: string;
    logoVersion: string;
    shortDescription: string;
    contractStatus: boolean;
    values: string;
    impactUrl: string;
    feeType: string;
    meta: {
      externalCommunity?: boolean;
      externalMoneyBuckets?: boolean;
      externalVettingClientImage?: string;
      externalMatch?: ExternalMatch;
      privacyPolicyCustom?: string;
      externalPrivacy?: boolean;
      eventComments?: boolean;
      eventCharity?: boolean;
      eventTeamAssociation?: boolean;
      eventRsvpInternal?: boolean;
      eventRsvpExternal?: boolean;
      eventNotifications?: boolean;
      eventPastOptions?: boolean;
      gtmContainerId?: string;
      initialUri?: string;
      navActivity?: boolean;
      navDonate?: boolean;
      navCompany?: boolean;
      navVolunteer?: boolean;
      navProfileName?: string;
      notificationSettings?: boolean;
      profileFunds?: boolean;
      profileImpact?: boolean;
      profileNotifications?: boolean;
      showFundsGlobal?: boolean;
      showInspireComments?: boolean;
      storyCtaCard?: {
        title?: string;
        subtitle?: string;
        buttonText?: string;
      };
      storyShareSansEvents?: boolean;
      storyComments?: boolean;
      storyMentions?: boolean;
      storyCharity?: boolean;
      storyTimeTrack?: boolean;
      storiesFeed?: boolean;
      storyRedirectUri?: string;
      socialSharing?: boolean;
      volunteerStoriesTab?: boolean;
      teamName?: boolean;
      searchVisibility?: boolean;
      externalCampaign?: ExternalCampaign;
      communityBoardStatus?: string;
      activation?: Activation;
      legacyBoard?: boolean;
      thankYouNoteDisclaimer?: string;
    };
  };
  group: Group;
  totalDonated: string;
  subjectType: string;
  subjectId: number;
}

export interface ExternalMatch {
  matchHeadline?: string;
  matchSubheadline?: string;
  matchButtonText?: string;
  matchButtonURl?: string; // This was likely a typo in spelling. Added matchButtonUrl as an option as well.
  matchButtonUrl?: string;
  matchSponsorText?: string;
  matchSponsorIcon?: string;
  matchNextGoal?: string;
  matchNextGoalSubheadline?: string;
  matchSecondaryCtaUrl?: string;
  matchSecondaryCtaText?: string;
}

export interface Meta {
  social?: Social;
  brand?: Brand;
  eventDetailCta?: {
    id: string;
    ctaAction: string;
    ctaTitle: string; // Refers to the text on the CTA button.
    title: string; // Refers to the text in the caption on the card.
  };
  marketing?: Marketing;
  links?: LinksEntity[] | null;
  seo?: Seo;
  stats?: Stats;
  feeds?: Feeds;
  footerBanner?: FooterBanner;
  options?: Options;
  externalMatch?: ExternalMatch;
}
export interface Social {
  twitterHandle?: string;
  siteName?: string;
  hashtags?: string;
}
export interface Brand {
  colors?: DefaultColors;
  tabIcon?: string;
  logo?: string;
  logoId?: string;
  logoVersion?: string;
  logoHeight?: string;
  themeStyle?: string;
}
export interface Marketing {
  shareCta?: string;
  keyword?: string;
  keywordUpper?: string;
}
export interface LinksEntity {
  hideMainNav?: boolean;
  icon?: string;
  title?: string;
  action?: string;
  target?: string;
}
export interface Seo {
  keywords?: string;
  description?: string;
  image?: string;
  organization?: string;
  site_name?: string;
  url?: string;
}
export interface Stats {
  title?: string;
  subtitle?: string;
  image?: string;
  statistics?: any;
  ctaText?: string;
  ctaURL?: string;
  backgroundColor?: string;
}
export interface Feeds {
  ratio?: number[] | null;
  paginationLength?: number;
  ctas?: CtasEntity[] | null;
}
export interface CtasEntity {
  index?: number;
  type?: string;
  title?: string;
  cta?: string;
  href?: string;
  image?: string;
}
export interface FooterBanner {
  title?: string;
  image?: string;
  ctaTitle?: string;
  ctaAction?: string;
  backgroundColor?: string;
  target: "_blank" | null;
}
export interface Options {
  hideMainNav?: boolean;
  hideStoryShareCTA?: boolean;
  storyShareCtaLineOne?: string;
  storyShareCtaLineTwo?: string;
  staticGeneration?: {
    revalidate?: number;
    prerenderPage?: boolean;
  };
}
export interface ExternalCampaign {
  campaignCompanyFieldName?: string;
  campaignCompanyFieldReq?: boolean;
  defaultDonation1?: string;
  defaultDonation2?: string;
  defaultDonation3?: string;
  defaultDonation4?: string;
  termsOfUseOrganization?: string;
  donationFeesCoverage?: boolean;
  donationFeesCoverageLogo?: string;
}
export interface Activation {
  type?: string;
  timestamp?: string;
}
export interface Group {
  id?: number;
  name?: string;
}

export interface CommunityCustomData {
  cta?: string;
  href?: string;
  image?: string;
  target?: string;
  title?: string;
}

export interface CommunityCustomDataFeedItem extends CommunityCustomData {
  type: "feedcta";
}

export type FeedItem =
  | CommunityCustomDataFeedItem
  | EventCommunityFeedItem
  | StoryCommunityFeedItem
  | CollectionCommunityFeedItem;
export interface CommunityFeed {
  totalRecords: number;
  records: Array<FeedItem>;
  next?: string;
}

export enum CommunityFeedType {
  FeedCTA = "feedcta",
  Story = "story",
  Event = "event",
  Collection = "collection",
}
export interface CommunityStories {
  records: Story[];
  totalRecords: number;
}

export interface CommunityEvents {
  records: Event[];
  totalRecords: number;
}

export interface CommunitySubCommunities {
  records: Community[];
  totalRecords: number;
}
